import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import GeneralInput from "../../common/GeneralInput";
import Modal from "../../common/Modal";
import AdminService from "../../../services/admin.service";
import { useAdmin } from "../../../context/admin";
import { Zone } from "../../../types/common.types";
import { AiOutlineCloseCircle } from "react-icons/ai";
import Checkbox from "../../common/Checkbox";
import jsonToFormData from "../../../utils/jsonToFormData";
import FileInput from "../../common/FileInput";
import { useGlobal } from "../../../context/global";

// Function to convert image to WebP
async function convertToWebP(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    // Check if file is already WebP
    if (file.type === 'image/webp') {
      resolve(file);
      return;
    }

    // Check if file is a string (already uploaded file URL/path)
    if (typeof file === 'string') {
      // If it's already a string (file path/URL), return as is
      resolve(file as unknown as File);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0);
        
        // Convert to WebP with quality settings
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not convert image to WebP'));
              return;
            }
            
            // Create new File object with WebP extension
            const webpFile = new File(
              [blob],
              `${file.name.split('.')[0] || 'image'}.webp`,
              { type: 'image/webp' }
            );
            resolve(webpFile);
          },
          'image/webp',
          0.85 // 85% quality - adjust as needed
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
}

export default function EditZone() {
    const { editZoneId, setEditZoneId } = useAdmin()
    const { setIsLoading, setMessage } = useGlobal()
    const [data, setData] = useState<Zone>({} as Zone)

    useEffect(() => {
        async function getInfo() {
            try {
                const list: Zone[] = (await AdminService.listZones()).data
                const zone = list.find(z => z.id === editZoneId)
                if (!zone) return
                setData({...zone , new_id: zone.id})
            } catch (error) {
                console.log(error);
            }
        }
        getInfo()
    }, [])

    async function submit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setIsLoading(true)
        try {
            // Convert cover image to WebP before submitting if it's a File object
            let processedCover = data.cover;
            
            if (processedCover instanceof File) {
              try {
                processedCover = await convertToWebP(processedCover);
                setMessage({
                  type: 'success',
                  content: 'Cover image converted to WebP'
                });
                setTimeout(() => {
                  setMessage(undefined);
                }, 2000);
              } catch (error) {
                console.error('Error converting cover to WebP:', error);
                setMessage({
                  type: 'error',
                  content: 'Uploading cover image in original format'
                });
                // Keep original file if conversion fails
              }
            }
            
            const newData: Partial<Zone> = {
                id: data.id,
                new_id: data.new_id,
                navbar: data.navbar,
                cover: processedCover,
                overview: data.overview,
                overview_es: data.overview_es,
                overview_pt: data.overview_pt,
                description: data.description,
                description_es: data.description_es,
                description_pt: data.description_pt
            }
            
            const formData = jsonToFormData(newData)
            await AdminService.editZone(formData)
            window.location.reload()
        } catch (error: any) {
            console.log(error);
            setMessage({
              type: 'error',
              content: error.response?.data || 'Error updating zone'
            });
            setTimeout(() => {
              setMessage(undefined);
            }, 2500);
        } finally {
            setIsLoading(false);
        }
    }

    function onChange(e: ChangeEvent<HTMLInputElement>) {
        const value = e.target.value
        const name = e.target.name
        setData(prevData  => ({...prevData, [name]: value }))
    }

    // Function to handle file upload with WebP conversion
    const handleFileChange = async (newFile: File) => {
      try {
        setIsLoading(true);
        const webpFile = await convertToWebP(newFile);
        setData(prevData => ({...prevData, cover: webpFile}));
        setMessage({
          type: 'success',
          content: `Cover image converted to WebP (${Math.round(webpFile.size / 1024)}KB)`
        });
        setTimeout(() => {
          setMessage(undefined);
        }, 2000);
      } catch (error) {
        console.error('Error converting to WebP:', error);
        setMessage({
          type: 'error',
          content: 'Failed to convert image. Uploading original format.'
        });
        // Fallback: use original file
        setData(prevData => ({...prevData, cover: newFile}));
        setTimeout(() => {
          setMessage(undefined);
        }, 2000);
      } finally {
        setIsLoading(false);
      }
    };

    // Helper function to determine if cover is a File object or string
    const isFile = (cover: any): cover is File => {
      return cover instanceof File;
    };

    return (
        <Modal
        onSubmit={submit}
        >
            <div className="input-container">
                <Checkbox
                checked={data.navbar}
                label="Show in navbar"
                onChange={(checked: boolean) => setData(prevData => ({...prevData, navbar: checked}))}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                name="new_id"
                label="New ID"
                value={data?.new_id || ''}
                onChange={onChange}
                />
            </div>
            <div className="input-container">
                <FileInput
                value={data.cover}
                style={{height: '100px'}}
                label="Cover (will be converted to WebP)"
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={handleFileChange}
                deleteFile={() => setData(prevData => {
                    const { cover, ...rest } = prevData;
                    return rest as Zone;
                })}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                label="Overview (en)"
                value={data.overview || ''}
                name="overview"
                onChange={onChange}
                />
                <GeneralInput
                label="Overview (es)"
                value={data.overview_es || ''}
                name="overview_es"
                onChange={onChange}
                />
                <GeneralInput
                label="Overview (pt)"
                value={data.overview_pt || ''}
                name="overview_pt"
                onChange={onChange}
                />
            </div>
            <div className="input-container">
                <GeneralInput
                label="Description (en)"
                value={data.description || ''}
                name="description"
                onChange={onChange}
                />
                <GeneralInput
                label="Description (es)"
                value={data.description_es || ''}
                name="description_es"
                onChange={onChange}
                />
                <GeneralInput
                label="Description (pt)"
                value={data.description_pt || ''}
                name="description_pt"
                onChange={onChange}
                />
            </div>
            <div className="actions"
            style={{marginTop: '20px'}}
            >
                <button type="submit">Submit</button>
            </div>
            <div className="close" title="close"
            onClick={() => setEditZoneId(undefined)}
            >
                <AiOutlineCloseCircle/>
            </div>
        </Modal>
    )
}